<script setup lang="ts">
defineOptions({ inheritAttrs: false });

interface Props {
  fullScreen?: boolean;
  width?: string;
  closeRight?: boolean;
  growWithContent?: boolean;
  hideClose?: boolean;
}

withDefaults(defineProps<Props>(), {
  fullScreen: false,
  width: undefined,
  closeRight: false,
  growWithContent: false,
  hideClose: false,
});

const attrs = useAttrs();

const emit = defineEmits<{
  closeModal: [];
}>();

const modalBody = ref<HTMLElement | null>(null);

function handleEscape(e: KeyboardEvent) {
  if (e.key === "Escape") closeModal();
}

onMounted(() => {
  document.body.classList.add("no-scroll");
  document.documentElement.classList.add("no-scroll");
  document.addEventListener("keydown", handleEscape);
  nextTick(() => {
    if (modalBody.value) {
      modalBody.value.scrollTop = 0;
    }
  });
});

onUnmounted(() => {
  document.body.classList.remove("no-scroll");
  document.documentElement.classList.remove("no-scroll");
  document.removeEventListener("keydown", handleEscape);
});

function closeModal() {
  document.body.classList.remove("no-scroll");
  document.documentElement.classList.remove("no-scroll");
  emit("closeModal");
}
</script>

<template>
  <Teleport to="body">
    <div
      class="modal"
      :class="[attrs.class, { 'modal--grow': growWithContent }]"
    >
      <div
        class="modal__backdrop"
        @click="closeModal"
      />
      <div
        class="modal__dialog animate__animated animate__zoomIn animate__faster"
        :class="{ 'full-screen': fullScreen }"
        :style="width ? { width } : undefined"
      >
        <div
          class="modal__dialog--top-bar"
          :class="{ 'modal__dialog--top-bar-reverse': closeRight }"
        >
          <button
            v-if="!hideClose"
            type="button"
            class="modal__dialog--close"
            aria-label="Close"
            @click="closeModal"
          >
            <fa :icon="['fal', 'xmark']" />
          </button>
          <div
            v-if="$slots['header-actions']"
            class="modal__dialog--actions"
          >
            <slot name="header-actions" />
          </div>
        </div>
        <div
          v-if="$slots.header"
          class="modal__dialog--header"
          :class="{ 'modal__dialog--header-tight': closeRight }"
        >
          <slot name="header" />
        </div>
        <div
          ref="modalBody"
          class="modal__dialog--body"
        >
          <slot name="body" />
        </div>
        <div
          v-if="$slots.footer"
          class="modal__dialog--footer"
        >
          <slot name="footer" />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.modal {
  transition: 0.5s;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 9999;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  @media (max-width: $tablet) {
    width: 100%;
  }
  &--grow {
    padding: 5vh 0;
    .modal__dialog {
      max-height: none;
      margin: auto;
    }
  }
  &__backdrop {
    position: fixed;
    overflow: unset;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: var(--color-grey-2);
    opacity: 0.6;
    z-index: 4444;
  }
  &__dialog {
    position: relative;
    width: 40%;
    background-color: var(--color-primary);
    border-radius: 0.4rem;
    overflow: hidden;
    max-height: 90vh;
    border: 2px solid var(--color-grey-3);
    display: flex;
    flex-direction: column;
    z-index: 4444;
    @media (max-width: $tablet) {
      width: 80%;
      max-height: 100vh;
      height: auto;
      margin: 0;
      overflow: unset;
    }
    &--top-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1rem 1.25rem;
      gap: 1rem;
    }
    &--top-bar-reverse {
      flex-direction: row-reverse;
      padding-left: 5%;
      padding-bottom: 0.2rem;
      .modal__dialog--close {
        position: relative;
        z-index: 1;
        margin: -0.7rem 0;
      }
      .modal__dialog--actions {
        flex: 1;
        justify-content: flex-start;
        min-width: 0;
      }
      :slotted(h3) {
        margin: 0;
        overflow-wrap: anywhere;
      }
    }
    // Plain clickable icon — no background, no hover pill, no button chrome.
    &--close {
      appearance: none;
      -webkit-appearance: none;
      background: transparent;
      border: 0;
      color: var(--color-grey-1);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0.75rem;
      margin: 0;
      // Guarantee an accessible/tappable hit area on touch devices —
      // 5% on a small mobile dialog was below the 44px minimum.
      min-width: var(--tap);
      min-height: var(--tap);
      font: inherit;
      cursor: pointer;
      line-height: 1;
      transition: color var(--dur-fast) var(--ease-out);
      svg {
        font-size: 1.2rem;
        width: 1.2rem;
        height: 1.2rem;
      }
      &:hover,
      &:focus {
        background: transparent;
        color: var(--color-pink-1);
        outline: none;
        box-shadow: none;
      }
      &:focus-visible {
        color: var(--color-pink-1);
      }
    }
    &--actions {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: 0.5rem;
      align-items: center;
    }
    &--header {
      background-color: var(--color-primary);
      padding: 0 5%;
      width: 90%;
    }
    &--header-tight :slotted(:first-child) {
      margin-top: 0;
    }
    &--body {
      display: flex;
      flex-direction: column;
      justify-content: space-around;
      padding: 0 5% 2rem;
      overflow-y: auto;
      top: 15%;
      height: 90%;
      -webkit-overflow-scrolling: touch;
      overflow-x: hidden;
      width: 90%;
    }
    &--footer {
      height: 20%;
      background-color: var(--color-primary);
      padding: 5%;
      bottom: 0;
      width: 90%;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
  }
  .full-screen {
    @media (max-width: $tablet) {
      width: 100%;
      height: 100% !important;
      border-radius: 0 !important;
      max-height: 100vh;
    }
  }
}
</style>
